import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { readPending, clearPending, pollConfirm } from "@/lib/checkout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Terms from "@/pages/Terms";
import TermsFull from "@/pages/TermsFull";
import ZipOnboard from "@/pages/ZipOnboard";
import MapPage from "@/pages/MapPage";
import LivePage from "@/pages/LivePage";
import FundraisersPage from "@/pages/FundraisersPage";
import GamesPage from "@/pages/GamesPage";
import GamePlay from "@/pages/GamePlay";
import NeighborMan from "@/pages/NeighborMan";
import SharePage from "@/pages/SharePage";
import ProfilePage from "@/pages/ProfilePage";
import DonatePage from "@/pages/DonatePage";
import ThanksPage from "@/pages/ThanksPage";
import ExternalSendPage from "@/pages/ExternalSendPage";
import BecomeRecipient from "@/pages/BecomeRecipient";
import OnboardBank from "@/pages/OnboardBank";

const Splash = () => (
  <div className="min-h-screen grid place-items-center bg-surface">
    <div className="flex flex-col items-center gap-4 animate-pop-in">
      <div className="w-16 h-16 rounded-pill grid place-items-center text-white animate-float" style={{ background: "#1E40AF" }}>
        <HeartHandshake size={34} strokeWidth={2.4} />
      </div>
      <div className="font-black text-2xl">Blessed</div>
    </div>
  </div>
);

const AppShell = () => {
  const location = useLocation();
  // If the user returns from Stripe on any page (not /thanks, which confirms itself),
  // finalize the pending donation so the feed/balance update without the webhook.
  useEffect(() => {
    if (location.pathname === "/thanks") return;
    const id = readPending();
    if (!id) return;
    let active = true;
    (async () => {
      const final = await pollConfirm(id);
      clearPending();
      if (active && (final === "sent" || final === "fee_paid")) toast.success("Your blessing is confirmed 💜");
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<MapPage />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/fundraisers" element={<Navigate to="/" replace />} />
        <Route path="/games" element={<GamesPage />} />
        <Route path="/games/neighbor-man" element={<NeighborMan />} />
        <Route path="/games/:gameId" element={<GamePlay />} />
        <Route path="/share" element={<SharePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/donate/external" element={<ExternalSendPage />} />
        <Route path="/donate/external/:id" element={<ExternalSendPage />} />
        <Route path="/donate/:recipientId" element={<DonatePage />} />
        <Route path="/thanks" element={<ThanksPage />} />
        <Route path="/become-recipient" element={<BecomeRecipient />} />
        <Route path="/onboard-bank" element={<OnboardBank />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
};

function AppRouter() {
  const location = useLocation();
  const { user, loading, acceptedTerms } = useAuth();

  if (location.hash?.includes("session_id=") || location.search?.includes("session_id=")) return <AuthCallback />;
  // Public legal page — always reachable, regardless of auth / acceptance state.
  if (location.pathname === "/terms") return <TermsFull />;
  if (loading) return <Splash />;
  if (!user) return <Login />;
  if (!acceptedTerms) return <Terms />;
  if (!user.zipcode) return <ZipOnboard />;
  return <AppShell />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
